<template>
  <layout>
    <div
      class="space-y-1 pb-4 pt-6 px-4 sm:p-6 bg-white flex-col space-y-2 items-center justify-between border-b border-b-gray-200"
    >
      <ArrowNarrowLeft
        class="w-6 sm:w-0 sm:hidden -ml-0.5 text-gray-900"
        @click="handleBack"
      />
      <div class="flex items-center justify-between">
        <h1 class="text-gray-900 text-xl font-medium">
          {{ t("admin.settings.messenger.title") }}
        </h1>
      </div>
    </div>
    <div class="dynamic-height overflow-auto bg-white">
      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="flex">
          <span
            class="flex gap-1.5 items-center font-medium text-blue-600 hover:cursor-pointer hover:text-blue-800 text-sm"
            @click="openMessenger"
          >
            <LinkOne class="w-4 h-4" />
            <span>{{ t("admin.settings.messenger.open") }}</span>
          </span>
        </div>

        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">{{
              t("admin.settings.messenger.require_contact_details_report.title")
            }}</span>
            <p class="text-gray-500 text-sm font-medium">
              {{
                t(
                  "admin.settings.messenger.require_contact_details_report.description"
                )
              }}
            </p>
          </div>
          <div class="flex flex-col space-y-4 items-end">
            <SwitchGroup as="div" class="flex items-center">
              <Switch
                v-model="settings.require_contact_details"
                :class="[
                  settings.require_contact_details
                    ? 'bg-gray-600'
                    : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  {
                    '!cursor-not-allowed ': !allowedToUpdate,
                  },
                ]"
                :disabled="!allowedToUpdate"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    settings.require_contact_details
                      ? 'translate-x-5'
                      : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>
        </div>
      </div>
      <div class="flex flex-col space-y-3 p-6 max-w-4xl">
        <div>
          <span class="text-sm font-medium">
            {{ t("admin.settings.messenger.required_details.title") }}
          </span>
          <p class="text-gray-500 text-sm font-medium">
            {{ t("admin.settings.messenger.required_details.description") }}
          </p>
        </div>
        <notification-options
          :active="settings.notification_options"
          :disabled="!allowedToUpdate"
          @option-selected="handleOptionSelected"
        />
      </div>
      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">{{
              t("admin.settings.messenger.require_name.title")
            }}</span>
            <p class="text-gray-500 text-sm font-medium">
              {{ t("admin.settings.messenger.require_name.description") }}
            </p>
          </div>
          <div class="flex flex-col space-y-4 items-end">
            <SwitchGroup as="div" class="flex items-center">
              <Switch
                v-model="settings.require_name"
                :class="[
                  settings.require_name ? 'bg-gray-600' : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  {
                    '!cursor-not-allowed ': !allowedToUpdate,
                  },
                ]"
                :disabled="!allowedToUpdate"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    settings.require_name ? 'translate-x-5' : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">
              {{ t("admin.settings.messenger.disable_space_selection.title") }}
            </span>
            <p class="text-gray-500 text-sm font-medium">
              {{
                t(
                  "admin.settings.messenger.disable_space_selection.description"
                )
              }}
            </p>
          </div>
          <div class="flex flex-col space-y-4 items-end">
            <SwitchGroup as="div" class="flex items-center">
              <Switch
                v-model="settings.disable_space_selection"
                :class="[
                  settings.disable_space_selection
                    ? 'bg-gray-600'
                    : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  { '!cursor-not-allowed': !allowedToUpdate },
                ]"
                :disabled="!allowedToUpdate"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    settings.disable_space_selection
                      ? 'translate-x-5'
                      : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">
              {{ t("admin.settings.messenger.hide_reported_problems.title") }}
            </span>
            <p class="text-gray-500 text-sm font-medium">
              {{
                t("admin.settings.messenger.hide_reported_problems.description")
              }}
            </p>
          </div>
          <div class="flex flex-col space-y-4 items-end">
            <SwitchGroup as="div" class="flex items-center">
              <Switch
                v-model="settings.hide_reported_problems"
                :class="[
                  settings.hide_reported_problems
                    ? 'bg-gray-600'
                    : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  { '!cursor-not-allowed': !allowedToUpdate },
                ]"
                :disabled="!allowedToUpdate"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    settings.hide_reported_problems
                      ? 'translate-x-5'
                      : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">
              {{ t("admin.settings.messenger.disable_notifications.title") }}
            </span>
            <p class="text-gray-500 text-sm font-medium">
              {{
                t("admin.settings.messenger.disable_notifications.description")
              }}
            </p>
          </div>
          <div class="flex flex-col space-y-4 items-end">
            <SwitchGroup as="div" class="flex items-center">
              <Switch
                v-model="settings.disable_notifications"
                :class="[
                  settings.disable_notifications
                    ? 'bg-gray-600'
                    : 'bg-gray-200',
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                  { '!cursor-not-allowed': !allowedToUpdate },
                ]"
                :disabled="!allowedToUpdate"
              >
                <span
                  aria-hidden="true"
                  :class="[
                    settings.disable_notifications
                      ? 'translate-x-5'
                      : 'translate-x-0',
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  ]"
                />
              </Switch>
            </SwitchGroup>
          </div>
        </div>
      </div>

      <div class="flex flex-col space-y-6 p-6 max-w-4xl">
        <div class="grid grid-cols-3 gap-x-10">
          <div class="col-span-2">
            <span class="text-sm font-medium">
              {{ t("admin.settings.messenger.languages.title") }}
            </span>
            <p class="text-gray-500 text-sm font-medium">
              {{ t("admin.settings.messenger.languages.description") }}
            </p>
          </div>
        </div>
        <div class="grid sm:grid-cols-4 gap-4">
          <SwitchGroup
            as="div"
            class="flex items-center"
            v-for="language in languages"
            :key="language.locale"
          >
            <Switch
              v-model="settings.allowed_messenger_locales[language.locale]"
              :class="[
                settings.allowed_messenger_locales[language.locale]
                  ? 'bg-gray-600'
                  : 'bg-gray-200',
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                { '!cursor-not-allowed': !allowedToUpdate },
              ]"
              :disabled="!allowedToUpdate"
            >
              <span
                aria-hidden="true"
                :class="[
                  settings.allowed_messenger_locales[language.locale]
                    ? 'translate-x-5'
                    : 'translate-x-0',
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                ]"
              />
            </Switch>
            <SwitchLabel as="span" class="ml-3">
              <span class="text-sm font-medium text-gray-900">{{
                language.label
              }}</span>
            </SwitchLabel>
          </SwitchGroup>
        </div>
      </div>
    </div>
  </layout>
</template>

<script setup>
import Layout from "@/Tailwind/Layouts/TenantSettings.vue";
import { Switch, SwitchGroup } from "@headlessui/vue";
import { usePage } from "@inertiajs/vue3";
import { computed, inject, watch } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import NotificationOptions from "@/Tailwind/Components/NotificationOptions.vue";
import { useI18n } from "vue-i18n";
import ArrowNarrowLeft from "@/Tailwind/Icon/ArrowNarrowLeft.vue";
import LinkOne from "@/Tailwind/Icon/LinkOne.vue";
import { useWebView } from "@/Tailwind/Mixins/useWebView";
import { mobileSatisfiesVersion } from "@/Tailwind/Helpers/version";

const tenant = usePage().props.tenant;
const settings = usePage().props.tenant.settings;

const { t, messages } = useI18n();
const { hasPermission, permissions } = inject("permissions");
const allowedToUpdate = hasPermission(permissions.updateMessengerFlow);

const { runningInWebView, openURL } = useWebView();
const openMessenger = () => {
  const url = route("space.select", [tenant]);

  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps.
  window.open(url, "_blank");
};

const languages = computed(() => {
  return Object.keys(messages.value).map((lang) => ({
    label: t(`validation.values.locale.${lang}`),
    locale: lang,
  }));
});

function handleOptionSelected(option) {
  settings.notification_options = option;
}

watch(settings, () => {
  router.patch(route("settings.messenger.update", [tenant]), {
    settings,
  });
});

function handleBack() {
  router.visit(route("settings.tenant", [tenant]));
}
</script>

<style lang="scss" scoped>
.dynamic-height {
  height: calc(100vh - 64px - 87px);
}
</style>
